const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom error class to preserve structured error data
class APIError extends Error {
  constructor(detail, data = null) {
    super(typeof detail === 'string' ? detail : JSON.stringify(detail));
    this.data = data || detail;
    this.isAPIError = true;
  }
}

const fetchWithCredentials = async (url, options = {}) => {
  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    // Preserve structured error data (e.g., for LIMIT_EXCEEDED)
    const detail = error.detail || error.message || 'Request failed';
    throw new APIError(detail, error.detail);
  }
  
  return response.json();
};

// Auth API
export const authApi = {
  me: () => fetchWithCredentials('/api/auth/me'),
  logout: () => fetchWithCredentials('/api/auth/logout', { method: 'POST' }),
  tenantInfo: () => fetchWithCredentials('/api/auth/tenant-info')
};

// Clients API
export const clientsApi = {
  list: () => fetchWithCredentials('/api/clients'),
  create: (data) => fetchWithCredentials('/api/clients', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchWithCredentials(`/api/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => fetchWithCredentials(`/api/clients/${id}`, { method: 'DELETE' }),
  checkName: (name) => fetchWithCredentials(`/api/clients/check-name?name=${encodeURIComponent(name)}`)
};

// Matches API
export const matchesApi = {
  list: (status) => fetchWithCredentials(`/api/matches${status ? `?status=${status}` : ''}`),
  get: (id) => fetchWithCredentials(`/api/matches/${id}`),
  create: (data) => fetchWithCredentials('/api/matches', { method: 'POST', body: JSON.stringify(data) }),
  declare: (id, winningTeam) => fetchWithCredentials(`/api/matches/${id}/declare`, { 
    method: 'POST', 
    body: JSON.stringify({ winning_team: winningTeam }) 
  }),
  undeclare: (id) => fetchWithCredentials(`/api/matches/${id}/revert`, { method: 'POST' }),
  delete: (id) => fetchWithCredentials(`/api/matches/${id}`, { method: 'DELETE' }),
  updateSettings: (id, settings) => fetchWithCredentials(`/api/matches/${id}/settings`, {
    method: 'PUT',
    body: JSON.stringify(settings)
  })
};

// Sessions API
export const sessionsApi = {
  list: (matchId, status) => {
    const params = new URLSearchParams();
    if (matchId) params.append('match_id', matchId);
    if (status) params.append('status', status);
    return fetchWithCredentials(`/api/sessions${params.toString() ? `?${params}` : ''}`);
  },
  create: (data) => fetchWithCredentials('/api/sessions', { method: 'POST', body: JSON.stringify(data) }),
  declare: (id, declaredRun) => fetchWithCredentials(`/api/sessions/${id}/declare`, { 
    method: 'POST', 
    body: JSON.stringify({ result: declaredRun }) 
  })
};

// Match Transactions API
export const matchTxApi = {
  list: (matchId, includeDeleted = false) => {
    const params = new URLSearchParams();
    if (matchId) params.append('match_id', matchId);
    if (includeDeleted) params.append('include_deleted', 'true');
    return fetchWithCredentials(`/api/tx/match${params.toString() ? `?${params}` : ''}`);
  },
  create: (data) => fetchWithCredentials('/api/tx/match', { method: 'POST', body: JSON.stringify(data) }),
  update: (txnId, data) => fetchWithCredentials(`/api/tx/match/${txnId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (txnId, reason) => fetchWithCredentials(`/api/tx/match/${txnId}/delete`, { method: 'POST', body: JSON.stringify({ reason: reason || null }) })
};

// Session Transactions API
export const sessionTxApi = {
  list: (sessionId, matchId, includeDeleted = false) => {
    const params = new URLSearchParams();
    if (sessionId) params.append('session_id', sessionId);
    if (matchId) params.append('match_id', matchId);
    if (includeDeleted) params.append('include_deleted', 'true');
    return fetchWithCredentials(`/api/tx/session${params.toString() ? `?${params}` : ''}`);
  },
  create: (data) => fetchWithCredentials('/api/tx/session', { method: 'POST', body: JSON.stringify(data) }),
  update: (txnId, data) => fetchWithCredentials(`/api/tx/session/${txnId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (txnId, reason) => fetchWithCredentials(`/api/tx/session/${txnId}/delete`, { method: 'POST', body: JSON.stringify({ reason: reason || null }) })
};

// Match Positions API
export const positionsApi = {
  list: (matchId, clientId) => {
    const params = new URLSearchParams();
    if (matchId) params.append('match_id', matchId);
    if (clientId) params.append('client_id', clientId);
    return fetchWithCredentials(`/api/match-positions${params.toString() ? `?${params}` : ''}`);
  }
};

// Online IDs API
export const onlineIdsApi = {
  list: () => fetchWithCredentials('/api/online-ids'),
  create: (data) => fetchWithCredentials('/api/online-ids', { method: 'POST', body: JSON.stringify(data) })
};

// Settlements API
export const settlementsApi = {
  list: (clientId) => fetchWithCredentials(`/api/settlements${clientId ? `?client_id=${clientId}` : ''}`),
  create: (data) => fetchWithCredentials('/api/settlements', { method: 'POST', body: JSON.stringify(data) }),
  getBalances: () => fetchWithCredentials('/api/settlements/balances')
};

// Monday Settling API
export const mondaySettlingApi = {
  getSettlements: () => fetchWithCredentials('/api/monday-settling/settlements'),
  createSettlements: (settlements) => fetchWithCredentials('/api/monday-settling/settlements', { 
    method: 'POST', 
    body: JSON.stringify({ settlements }) 
  }),
  updateSettlement: (id, data) => fetchWithCredentials(`/api/monday-settling/settlements/${id}`, { 
    method: 'PUT', 
    body: JSON.stringify(data) 
  }),
  deleteSettlement: (id) => fetchWithCredentials(`/api/monday-settling/settlements/${id}`, { 
    method: 'DELETE' 
  }),
  getReconciliation: () => fetchWithCredentials('/api/monday-settling/reconciliation')
};

// Reports API
export const reportsApi = {
  trialBalance: (filters) => fetchWithCredentials('/api/reports/trial-balance', { 
    method: 'POST', 
    body: JSON.stringify(filters) 
  }),
  netResult: (matchId) => fetchWithCredentials(`/api/reports/net-result${matchId ? `?match_id=${matchId}` : ''}`),
  exportNetResultPDF: async (matchId) => {
    const response = await fetch(`${API_URL}/api/reports/net-result/pdf${matchId ? `?match_id=${matchId}` : ''}`, {
      credentials: 'include',
      headers: {
        'Accept': 'application/pdf'
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'PDF export failed' }));
      throw new APIError(error.detail || 'PDF export failed', error.detail);
    }
    
    return response.blob();
  }
};

// Meta Transactions API
export const metaApi = {
  trades: {
    list: (instrumentType) => fetchWithCredentials(`/api/meta/trades${instrumentType ? `?instrument_type=${instrumentType}` : ''}`),
    create: (data) => fetchWithCredentials('/api/meta/trades', { method: 'POST', body: JSON.stringify(data) })
  },
  positions: {
    list: (clientId) => fetchWithCredentials(`/api/meta/positions${clientId ? `?client_id=${clientId}` : ''}`)
  },
  marketPrices: {
    list: () => fetchWithCredentials('/api/meta/market-prices'),
    set: (symbol, price) => fetchWithCredentials('/api/meta/market-prices', { 
      method: 'POST', 
      body: JSON.stringify({ symbol, price }) 
    })
  }
};

// MCX & F&O API
export const mcxApi = {
  scripts: {
    list: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/scripts${query ? `?${query}` : ''}`);
    },
    get: (id) => fetchWithCredentials(`/api/mcx/scripts/${id}`),
    create: (data) => fetchWithCredentials('/api/mcx/scripts', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => fetchWithCredentials(`/api/mcx/scripts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updatePrice: (id, price, source = 'MANUAL') => fetchWithCredentials(`/api/mcx/scripts/${id}/price`, { 
      method: 'PATCH', 
      body: JSON.stringify({ script_id: id, price, source }) 
    }),
    delete: (id) => fetchWithCredentials(`/api/mcx/scripts/${id}`, { method: 'DELETE' })
  },
  clients: {
    list: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/clients${query ? `?${query}` : ''}`);
    },
    get: (id) => fetchWithCredentials(`/api/mcx/clients/${id}`),
    create: (data) => fetchWithCredentials('/api/mcx/clients', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => fetchWithCredentials(`/api/mcx/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  trades: {
    list: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/trades${query ? `?${query}` : ''}`);
    },
    create: (data) => fetchWithCredentials('/api/mcx/trades', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => fetchWithCredentials(`/api/mcx/trades/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => fetchWithCredentials(`/api/mcx/trades/${id}`, { method: 'DELETE' })
  },
  positions: {
    list: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/positions${query ? `?${query}` : ''}`);
    },
    get: (clientId, scriptId) => fetchWithCredentials(`/api/mcx/positions/${clientId}/${scriptId}`)
  },
  settlements: {
    list: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/settlements${query ? `?${query}` : ''}`);
    },
    settle: (data) => fetchWithCredentials('/api/mcx/settlements/settle', { method: 'POST', body: JSON.stringify(data) })
  },
  reports: {
    tradeHistory: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/reports/trade-history${query ? `?${query}` : ''}`);
    },
    positionReport: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/reports/position-report${query ? `?${query}` : ''}`);
    },
    settlementReport: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/reports/settlement-report${query ? `?${query}` : ''}`);
    },
    brokerageReport: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/reports/brokerage-report${query ? `?${query}` : ''}`);
    }
  },
  bhavCopy: {
    status: () => fetchWithCredentials('/api/mcx/bhav-copy/status'),
    uploadCsv: (csvContent) => fetchWithCredentials('/api/mcx/bhav-copy/upload', {
      method: 'POST',
      body: JSON.stringify({ csv_content: csvContent })
    }),
    bulkUpdate: (prices) => fetchWithCredentials('/api/mcx/bhav-copy/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ prices })
    }),
    triggerScheduled: () => fetchWithCredentials('/api/mcx/bhav-copy/trigger-scheduled', { method: 'POST' }),
    priceHistory: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetchWithCredentials(`/api/mcx/bhav-copy/price-history${query ? `?${query}` : ''}`);
    }
  }
};

// Weekly Dollar API
export const weeklyDollarApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithCredentials(`/api/mcx-fo/weekly-dollar${query ? `?${query}` : ''}`);
  },
  getTemplateData: () => fetchWithCredentials('/api/mcx-fo/weekly-dollar/template-data'),
  create: (data) => fetchWithCredentials('/api/mcx-fo/weekly-dollar', { 
    method: 'POST', 
    body: JSON.stringify(data) 
  }),
  update: (id, data) => fetchWithCredentials(`/api/mcx-fo/weekly-dollar/${id}`, { 
    method: 'PUT', 
    body: JSON.stringify(data) 
  }),
  delete: (id) => fetchWithCredentials(`/api/mcx-fo/weekly-dollar/${id}`, { method: 'DELETE' }),
  bulkCreate: (entries) => fetchWithCredentials('/api/mcx-fo/weekly-dollar/bulk', {
    method: 'POST',
    body: JSON.stringify(entries)
  }),
  toggleTallied: (id, tallied) => fetchWithCredentials(`/api/mcx-fo/weekly-dollar/${id}/tallied?tallied=${tallied}`, { method: 'POST' }),
  settle: (id) => fetchWithCredentials(`/api/mcx-fo/weekly-dollar/${id}/settle`, { method: 'POST' }),
  settleBulk: (entryIds) => fetchWithCredentials('/api/mcx-fo/weekly-dollar/settle-bulk', {
    method: 'POST',
    body: JSON.stringify(entryIds)
  }),
  reverseSettlement: (id) => fetchWithCredentials(`/api/mcx-fo/weekly-dollar/${id}/reverse-settlement`, { method: 'POST' }),
  importPreview: (formData) => fetch(`${API_URL}/api/mcx-fo/weekly-dollar/import/preview`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  }).then(r => r.json()),
  importConfirm: (rows, entryDate) => fetchWithCredentials(`/api/mcx-fo/weekly-dollar/import/confirm${entryDate ? `?entry_date=${entryDate}` : ''}`, {
    method: 'POST',
    body: JSON.stringify(rows)
  }),
  downloadTemplate: () => `${API_URL}/api/mcx-fo/weekly-dollar/import/template`,
  exportXlsx: (entryDate) => `${API_URL}/api/mcx-fo/weekly-dollar/export/xlsx${entryDate ? `?entry_date=${entryDate}` : ''}`,
  exportPdf: (entryDate) => `${API_URL}/api/mcx-fo/weekly-dollar/export/pdf${entryDate ? `?entry_date=${entryDate}` : ''}`,
  recalculateAll: () => fetchWithCredentials('/api/mcx-fo/weekly-dollar/recalculate-all', { method: 'POST' })
};

// MCX Settlement API
export const mcxSettlementApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithCredentials(`/api/mcx-fo/mcx-settlement${query ? `?${query}` : ''}`);
  },
  getClientsList: () => fetchWithCredentials('/api/mcx-fo/mcx-settlement/clients-list'),
  viewByClient: (clientId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithCredentials(`/api/mcx-fo/mcx-settlement/view-by-client/${clientId}${query ? `?${query}` : ''}`);
  },
  viewByParent: (parentId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithCredentials(`/api/mcx-fo/mcx-settlement/view-by-parent/${parentId}${query ? `?${query}` : ''}`);
  },
  createManual: (data) => fetchWithCredentials('/api/mcx-fo/mcx-settlement/manual', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  delete: (id) => fetchWithCredentials(`/api/mcx-fo/mcx-settlement/${id}`, { method: 'DELETE' }),
  exportXlsx: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return `${API_URL}/api/mcx-fo/mcx-settlement/export/xlsx${query ? `?${query}` : ''}`;
  },
  exportPdf: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return `${API_URL}/api/mcx-fo/mcx-settlement/export/pdf${query ? `?${query}` : ''}`;
  }
};

// Dashboard API
export const dashboardApi = {
  stats: () => fetchWithCredentials('/api/dashboard/stats')
};

// Admin API
export const adminApi = {
  tenants: {
    list: () => fetchWithCredentials('/api/admin/tenants'),
    update: (id, data) => fetchWithCredentials(`/api/admin/tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  users: {
    list: () => fetchWithCredentials('/api/admin/users'),
    update: (id, data) => fetchWithCredentials(`/api/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    resetPassword: (id) => fetchWithCredentials(`/api/admin/users/${id}/reset-password`, { method: 'POST' }),
    delete: (id) => fetchWithCredentials(`/api/admin/users/${id}`, { method: 'DELETE' })
  },
  stats: () => fetchWithCredentials('/api/admin/stats'),
  auditLog: (limit = 100) => fetchWithCredentials(`/api/admin/audit-log?limit=${limit}`)
};

// Dabba API
export const dabbaApi = {
  ledger: (clientId, matchId) => {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId);
    if (matchId) params.append('match_id', matchId);
    return fetchWithCredentials(`/api/dabba/ledger${params.toString() ? `?${params}` : ''}`);
  },
  positions: (clientId) => fetchWithCredentials(`/api/dabba/positions${clientId ? `?client_id=${clientId}` : ''}`),
  calculate: (data) => fetchWithCredentials('/api/dabba/calculate', { method: 'POST', body: JSON.stringify(data) })
};

// Reference Commission API
export const referenceApi = {
  commissions: (clientId, referenceId) => {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId);
    if (referenceId) params.append('reference_id', referenceId);
    return fetchWithCredentials(`/api/reference/commissions${params.toString() ? `?${params}` : ''}`);
  },
  summary: () => fetchWithCredentials('/api/reference/summary'),
  calculate: (data) => fetchWithCredentials('/api/reference/calculate', { method: 'POST', body: JSON.stringify(data) })
};

// Quick Entry API
export const quickEntryApi = {
  recentClients: (limit = 10) => fetchWithCredentials(`/api/quick-entry/recent-clients?limit=${limit}`),
  searchClients: (q, limit = 5) => fetchWithCredentials(`/api/quick-entry/client-search?q=${encodeURIComponent(q)}&limit=${limit}`),
  lastTransaction: (clientId, matchId) => {
    const params = new URLSearchParams({ client_id: clientId });
    if (matchId) params.append('match_id', matchId);
    return fetchWithCredentials(`/api/quick-entry/last-transaction?${params}`);
  },
  batch: (transactions) => fetchWithCredentials('/api/quick-entry/batch', { method: 'POST', body: JSON.stringify({ transactions }) })
};
